/**
 * Dock Load Balancing Engine (Deterministic, Read-Only)
 *
 * Detects uneven dock pressure in the next 4 hours and suggests
 * non-destructive appointment rebalancing moves.
 *
 * No mutations, no AI language, no backend changes.
 * Reuses getRankedDockAssignments for target scoring.
 */
import type { Appointment } from '../types/appointments';
import { safeDateMs } from './cascadePrediction';
import { getRankedDockAssignments } from './predictiveDockAssignment';
import { extractPOMetadataForScoring } from './poMetadataHelpers';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DockLoadBalanceSuggestion {
    appointment: Appointment;
    fromDockId: string;
    fromDockNumber: string;
    toDockId: string;
    toDockNumber: string;
    reasons: string[];
    beforeLoad: number;
    afterLoad: number;
    beforeStacking: number;
    afterStacking: number;
    improvementScore: number;
}

export interface DockLoadBalanceResult {
    suggestions: DockLoadBalanceSuggestion[];
    totalOpportunities: number;
}

interface BalancingDock {
    id: string;
    dockNumber: string;
    type: string;
    status?: string | null;
    // INV-4.1: Capability tags for compatibility scoring
    temperatureZone?: string | null;
    handlingCapabilities?: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EXCLUDED_STATUSES = new Set(['cancelled', 'declined', 'completed']);
const IMMOVABLE_STATUSES = new Set(['checked_in', 'in_progress', 'completed', 'cancelled', 'declined']);

function getDockId(appt: Appointment): string | null {
    return appt.dockId || appt.dock_id || null;
}

/**
 * Build a map: dockId → count of appointments in a given hour window.
 */
function countAppointmentsPerDock(
    schedule: Appointment[],
    hourStart: number,
    hourEnd: number,
): Map<string, Appointment[]> {
    const map = new Map<string, Appointment[]>();
    for (const appt of schedule) {
        const status = (appt.status || '').toLowerCase();
        if (EXCLUDED_STATUSES.has(status)) continue;

        const dockId = getDockId(appt);
        if (!dockId) continue;

        const startMs = safeDateMs(appt.startAt);
        if (isNaN(startMs)) continue;

        if (startMs >= hourStart && startMs < hourEnd) {
            const list = map.get(dockId) || [];
            list.push(appt);
            map.set(dockId, list);
        }
    }
    return map;
}

// ─── Engine ───────────────────────────────────────────────────────────────────

export function computeDockLoadBalancing(
    todaySchedule: Appointment[],
    docks: BalancingDock[],
    now: number,
    horizonHours: number = 4,
    warehouseTimeZone?: string
): DockLoadBalanceResult {
    if (docks.length < 2) {
        return { suggestions: [], totalOpportunities: 0 };
    }

    const suggestions: DockLoadBalanceSuggestion[] = [];
    const seenApptIds = new Set<string>();
    const hourMs = 60 * 60 * 1000;
    const MIN_LEAD_TIME_MS = 30 * 60 * 1000; // 30 min — don't suggest moves for imminent appointments

    // Build dock lookup
    const dockById = new Map<string, BalancingDock>();
    for (const d of docks) dockById.set(d.id, d);

    // Scan each hour in the horizon
    for (let h = 0; h < horizonHours; h++) {
        const hourStart = now + h * hourMs;
        const hourEnd = hourStart + hourMs;

        const dockAppts = countAppointmentsPerDock(todaySchedule, hourStart, hourEnd);

        // Compute average load across docks that have appointments
        const activeDockCount = docks.length;
        let totalAppts = 0;
        for (const [, appts] of dockAppts) totalAppts += appts.length;
        const avgLoad = totalAppts / activeDockCount;

        // Find overloaded docks: load >= threshold, clamped to at least 2
        const threshold = Math.max(avgLoad + 2, 2);
        for (const [dockId, appts] of dockAppts) {
            if (appts.length < threshold) continue;

            const sourceDock = dockById.get(dockId);
            if (!sourceDock) continue;

            // Find movable candidates (only approved/scheduled, not checked in, not starting soon)
            const movable = appts.filter(a => {
                const s = (a.status || '').toLowerCase();
                if (IMMOVABLE_STATUSES.has(s)) return false;
                if (seenApptIds.has(a.id)) return false;
                // Skip appointments starting within 30 minutes
                const startMs = safeDateMs(a.startAt);
                if (!isNaN(startMs) && startMs < now + MIN_LEAD_TIME_MS) return false;
                return true;
            });

            // Pick up to 2 move candidates
            for (const candidate of movable.slice(0, 2)) {
                // Find best target dock using existing scoring engine
                // Simulate: exclude the source dock from candidates
                const targetDocks = docks.filter(d => d.id !== dockId);
                // INV-4.1: Pass PO compatibility metadata for temperature/handling scoring
                const poMeta = extractPOMetadataForScoring(candidate.linkedPOs);
                const ranked = getRankedDockAssignments(candidate, targetDocks, todaySchedule, [], now, warehouseTimeZone, poMeta);
                const bestTarget = ranked.find(c => c.isEligible && c.score > 0 && c.dockId !== dockId);
                if (!bestTarget) continue;

                // Compute before/after metrics
                const beforeStacking = appts.length;
                const afterStacking = beforeStacking - 1;

                // Target dock stacking
                const targetAppts = dockAppts.get(bestTarget.dockId) || [];
                const targetAfter = targetAppts.length + 1;

                // Only suggest if it actually improves things
                if (afterStacking <= targetAfter && beforeStacking <= 2) continue;

                const beforeLoad = Math.round((beforeStacking / 1) * 100); // % of single dock
                const afterLoad = Math.round((afterStacking / 1) * 100);

                // Build reasons
                const reasons: string[] = [];
                reasons.push('Reduces overload');
                for (const r of bestTarget.reasons.slice(0, 2)) {
                    reasons.push(r.label);
                }

                const improvementScore = (beforeStacking - afterStacking) * 50
                    + bestTarget.score
                    - (targetAfter > 1 ? 20 : 0);

                suggestions.push({
                    appointment: candidate,
                    fromDockId: dockId,
                    fromDockNumber: sourceDock.dockNumber,
                    toDockId: bestTarget.dockId,
                    toDockNumber: bestTarget.dockNumber,
                    reasons: reasons.slice(0, 3),
                    beforeLoad,
                    afterLoad,
                    beforeStacking,
                    afterStacking,
                    improvementScore,
                });

                seenApptIds.add(candidate.id);
            }
        }
    }

    // Sort by improvement score descending
    suggestions.sort((a, b) => b.improvementScore - a.improvementScore);

    return {
        suggestions,
        totalOpportunities: suggestions.length,
    };
}
